import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseEntityHttpService } from '../../shared/base.httpservice';
import { ListRequestOptions, ResourceLoadResponse } from '../../shared/state';
import { Team } from '../../models/team';
import { TeamPlayer } from '../../models/teamplayer';

// Root-provided (rather than scoped to TeamsModule like the other feature modules' HTTP services) so
// the profile page's favorite teams/invites cards can use it too, without pulling in the whole
// TeamsModule (and its own 'teams' ngrx feature state) - see PlayersHttpService/StatisticsHttpService in
// app.module.ts for the same reasoning.
@Injectable({
	providedIn: 'root'
})
export class TeamsHttpService extends BaseEntityHttpService<Team> {
	protected endpoint = this.apiUrl('teams');

	constructor(http: HttpClient) {
		super(http);
	}

	load(query?: ListRequestOptions): Observable<ResourceLoadResponse<Team>> {
		return this.http.get<ResourceLoadResponse<Team>>(`${this.endpoint}/list`, { params: query as any });
	}

	getById(id: string): Observable<Team> {
		return this.http.get<Team>(`${this.endpoint}/${id}`);
	}

	getMine(): Observable<Team[]> {
		return this.http.get<Team[]>(`${this.endpoint}/mine`);
	}

	// Teams the current user captains with exactly 2 accepted players - the only teams eligible to
	// enter a Koppeltoernooi.
	getMinePairs(): Observable<Team[]> {
		return this.http.get<Team[]>(`${this.endpoint}/mine/pairs`);
	}

	getFavorites(): Observable<Team[]> {
		return this.http.get<Team[]>(`${this.endpoint}/favorites`);
	}

	getMyInvites(): Observable<TeamPlayer[]> {
		return this.http.get<TeamPlayer[]>(`${this.endpoint}/invites`);
	}

	addTeam(team: Team): Observable<Team> {
		return this.http.post<Team>(this.endpoint, team);
	}

	editTeam(team: Team): Observable<Team> {
		return this.http.put<Team>(this.endpoint, team);
	}

	favorite(teamId: string): Observable<any> {
		return this.http.post(`${this.endpoint}/${teamId}/favorite`, {});
	}

	unfavorite(teamId: string): Observable<any> {
		return this.http.delete(`${this.endpoint}/${teamId}/favorite`);
	}

	invite(teamId: string, userId: string): Observable<TeamPlayer> {
		return this.http.post<TeamPlayer>(`${this.endpoint}/${teamId}/invite`, { userId });
	}

	acceptInvite(teamPlayerId: string): Observable<any> {
		return this.http.post(`${this.endpoint}/invites/${teamPlayerId}/accept`, {});
	}

	declineInvite(teamPlayerId: string): Observable<any> {
		return this.http.post(`${this.endpoint}/invites/${teamPlayerId}/decline`, {});
	}

	removePlayer(teamId: string, userId: string): Observable<any> {
		return this.http.delete(`${this.endpoint}/${teamId}/players/${userId}`);
	}

	transferCaptain(teamId: string, newCaptainUserId: string): Observable<any> {
		return this.http.post(`${this.endpoint}/${teamId}/transfercaptain`, { newCaptainUserId });
	}
}
